import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, Check, CheckCircle2, Clock, MessageCircle, Phone, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, pick, waLink, PHONE_TEL, PHONE_DISPLAY } from "../lib/api";
import { waBookingText } from "../lib/i18n";
import { useLang } from "../lib/i18n";
import { FadeIn } from "../components/Reveal";
import { Calendar } from "../components/ui/calendar";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

const fmt = (n) => `Rs ${Number(n).toLocaleString("en-MU")}`;
const TIMES = ["08:00", "09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00"];

const Stepper = ({ step, labels }) => (
  <div className="flex items-center gap-1.5 sm:gap-2.5 flex-wrap" data-testid="booking-stepper">
    {labels.map((l, i) => (
      <React.Fragment key={i}>
        <div className={`flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-semibold transition-colors duration-300 ${step === i + 1 ? "bg-forest-700 text-white" : step > i + 1 ? "bg-mint text-forest-800" : "bg-mist text-muted-foreground"}`}>
          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step > i + 1 ? "bg-forest-600 text-white" : step === i + 1 ? "bg-white/20" : "bg-forest-900/10"}`}>
            {step > i + 1 ? <Check size={11} strokeWidth={3.5} /> : i + 1}
          </span>
          <span className="hidden sm:inline">{l}</span>
        </div>
        {i < labels.length - 1 && <div className={`h-px w-3 sm:w-6 ${step > i + 1 ? "bg-forest-400" : "bg-forest-100"}`} />}
      </React.Fragment>
    ))}
  </div>
);

export default function Booking() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then(r => r.data) });

  const [step, setStep] = useState(1);
  const [service, setService] = useState(params.get("service") || "");
  const [pkg, setPkg] = useState(params.get("package") || "");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", notes: "" });
  const [saved, setSaved] = useState(null);
  const [sending, setSending] = useState(false);

  const labels = [t.booking.step1, t.booking.step2, t.booking.step3, t.booking.step4, t.booking.review];
  const s = services.find(x => x.slug === service);
  const pkgName = pkg === "full" ? t.booking.full : t.booking.half;
  const price = s ? (pkg === "full" ? s.full_price : s.half_price) : 0;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canNext = useMemo(() => {
    if (step === 1) return !!service;
    if (step === 2) return !!pkg;
    if (step === 3) return !!date && !!time;
    if (step === 4) return form.name.trim() && form.phone.trim() && form.address.trim();
    return true;
  }, [step, service, pkg, date, time, form]);

  const next = () => { if (!canNext) { toast.error(t.booking.required); return; } setStep(step + 1); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => setStep(Math.max(1, step - 1));

  const confirm = async () => {
    setSending(true);
    try {
      const payload = {
        service_slug: s.slug, service_name: pick(s, "name", lang), package: pkg,
        date: format(date, "yyyy-MM-dd"), time,
        name: form.name, phone: form.phone, email: form.email, address: form.address, notes: form.notes,
      };
      const res = await api.post("/bookings", payload);
      setSaved(res.data);
      setStep(6);
      window.open(waLink(waBookingText(lang, {
        service: pick(s, "name", lang), pkg: pkgName, date: format(date, lang === "fr" ? "dd/MM/yyyy" : "EEE d MMM yyyy"),
        time, name: form.name, phone: form.phone, address: form.address, notes: form.notes,
      })), "_blank");
    } catch (e) {
      toast.error(t.common.error);
    } finally {
      setSending(false);
    }
  };

  const reset = () => { setStep(1); setService(""); setPkg(""); setDate(null); setTime(""); setForm({ name: "", phone: "", email: "", address: "", notes: "" }); setSaved(null); };

  const inputCls = "mt-2";

  return (
    <div className="pt-[72px] min-h-screen bg-mist/60" data-testid="booking-page">
      <div className="max-w-3xl mx-auto px-6 py-14 md:py-20">
        <FadeIn>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold text-forest-950">{t.booking.title}</h1>
          <p className="mt-3 text-body text-lg">{t.booking.sub}</p>
        </FadeIn>

        {step <= 5 && (
          <FadeIn delay={0.1}>
            <div className="mt-9"><Stepper step={step} labels={labels} /></div>
          </FadeIn>
        )}

        <div className="mt-10 bg-white rounded-[28px] border border-forest-100 shadow-sm p-7 md:p-10" data-testid="booking-card">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
                <div className="grid sm:grid-cols-2 gap-4">
                  {services.map((sv) => (
                    <button key={sv.slug} onClick={() => { setService(sv.slug); }} data-testid={`pick-service-${sv.slug}`}
                      className={`text-left rounded-2xl border-2 p-5 transition-all duration-200 hover:-translate-y-0.5 ${service === sv.slug ? "border-forest-600 bg-mint shadow-sm" : "border-forest-100 hover:border-forest-300"}`}>
                      <div className="font-display font-semibold text-forest-900">{pick(sv, "name", lang)}</div>
                      <div className="text-xs text-muted-foreground mt-1">{pick(sv, "unit", lang)}</div>
                      <div className="mt-3 text-sm font-bold text-forest-700">{t.services.from} {fmt(sv.half_price)}</div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 2 && s && (
              <motion.div key="s2" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
                <h3 className="font-display text-xl font-semibold text-forest-900">{t.booking.choosePackage}</h3>
                <div className="mt-5 grid sm:grid-cols-2 gap-4">
                  {[
                    { key: "full", title: t.booking.full, desc: t.booking.fullDesc, price: s.full_price, dark: true, icon: Sparkles },
                    { key: "half", title: t.booking.half, desc: t.booking.halfDesc, price: s.half_price, dark: false, icon: CheckCircle2 },
                  ].map((c) => (
                    <button key={c.key} onClick={() => setPkg(c.key)} data-testid={`pick-package-${c.key}`}
                      className={`text-left rounded-2xl border-2 p-6 transition-all duration-200 hover:-translate-y-0.5 ${pkg === c.key ? (c.dark ? "border-forest-600 bg-forest-700 text-white shadow-lg" : "border-forest-600 bg-mint") : "border-forest-100 hover:border-forest-300"}`}>
                      <div className="flex items-center gap-2">
                        <c.icon size={18} className={c.dark && pkg === c.key ? "text-forest-200" : "text-forest-600"} />
                        <span className="font-display font-semibold text-lg">{c.title}</span>
                      </div>
                      <div className={`mt-3 font-display text-3xl font-bold ${c.dark && pkg === c.key ? "text-white" : "text-forest-800"}`}>{fmt(c.price)}</div>
                      <div className="text-xs text-muted-foreground mt-1">{pick(s, "unit", lang)}</div>
                      <p className={`mt-3 text-sm leading-relaxed ${c.dark && pkg === c.key ? "text-forest-100/90" : "text-body"}`}>{c.desc}</p>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
                <h3 className="font-display text-xl font-semibold text-forest-900 flex items-center gap-2"><CalendarDays size={19} className="text-forest-600" /> {t.booking.pickDate}</h3>
                <div className="mt-4 rounded-2xl border border-forest-100 p-4 inline-block" data-testid="booking-calendar">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: new Date() }} classNames={{ day_selected: "bg-forest-700 text-white hover:bg-forest-800 hover:text-white focus:bg-forest-700", day_today: "bg-mint text-forest-900" }} />
                </div>
                <h3 className="mt-8 font-display text-xl font-semibold text-forest-900 flex items-center gap-2"><Clock size={19} className="text-forest-600" /> {t.booking.pickTime}</h3>
                <div className="mt-4 flex flex-wrap gap-2.5">
                  {TIMES.map((tm) => (
                    <button key={tm} onClick={() => setTime(tm)} data-testid={`pick-time-${tm}`}
                      className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${time === tm ? "bg-forest-700 text-white" : "bg-mist text-forest-900 hover:bg-mint"}`}>
                      {tm}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="s4" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-semibold text-forest-900">{t.booking.name} *</label>
                    <Input className={inputCls} value={form.name} onChange={set("name")} placeholder={t.booking.namePh} data-testid="booking-name-input" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-forest-900">{t.booking.phone} *</label>
                    <Input className={inputCls} value={form.phone} onChange={set("phone")} placeholder={t.booking.phonePh} data-testid="booking-phone-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-sm font-semibold text-forest-900">{t.booking.email}</label>
                    <Input className={inputCls} type="email" value={form.email} onChange={set("email")} placeholder={t.booking.emailPh} data-testid="booking-email-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-sm font-semibold text-forest-900">{t.booking.address} *</label>
                    <Input className={inputCls} value={form.address} onChange={set("address")} placeholder={t.booking.addressPh} data-testid="booking-address-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-sm font-semibold text-forest-900">{t.booking.notes}</label>
                    <Textarea className={inputCls} rows={3} value={form.notes} onChange={set("notes")} placeholder={t.booking.notesPh} data-testid="booking-notes-input" />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 5 && s && (
              <motion.div key="s5" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
                <h3 className="font-display text-xl font-semibold text-forest-900">{t.booking.review}</h3>
                <p className="text-sm text-muted-foreground mt-1">{t.booking.reviewSub}</p>
                <dl className="mt-6 rounded-2xl bg-mist p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-testid="booking-review">
                  {[
                    [t.admin.col.service, pick(s, "name", lang)],
                    [t.booking.step2, `${pkgName} · ${fmt(price)}`],
                    [t.admin.col.when, `${date ? format(date, lang === "fr" ? "dd/MM/yyyy" : "EEE d MMM yyyy") : ""} · ${time}`],
                    [t.booking.name, form.name],
                    [t.booking.phone, form.phone],
                    ...(form.email ? [[t.booking.email, form.email]] : []),
                    [t.booking.address, form.address],
                    ...(form.notes ? [[t.booking.notes, form.notes]] : []),
                  ].map(([k, v], i) => (
                    <div key={i}>
                      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{k}</dt>
                      <dd className="text-sm font-semibold text-forest-900 mt-1">{v}</dd>
                    </div>
                  ))}
                </dl>
              </motion.div>
            )}

            {step === 6 && (
              <motion.div key="s6" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35 }} className="text-center py-6">
                <div className="mx-auto w-16 h-16 rounded-full bg-mint flex items-center justify-center" data-testid="booking-success">
                  <CheckCircle2 size={34} className="text-forest-600" />
                </div>
                <h3 className="mt-5 font-display text-3xl font-bold text-forest-950">{t.booking.done}</h3>
                <p className="mt-2 text-body max-w-md mx-auto">{t.booking.doneSub}</p>
                <a href={waLink(waBookingText(lang, {
                  service: pick(s, "name", lang), pkg: pkgName, date: date ? format(date, "EEE d MMM yyyy") : "", time, name: form.name, phone: form.phone, address: form.address, notes: form.notes,
                }))} target="_blank" rel="noopener noreferrer" data-testid="booking-wa-send"
                  className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-8 py-4 font-semibold text-forest-950 shadow-lg shadow-emerald-600/20 hover:-translate-y-0.5 transition-all">
                  <MessageCircle size={19} /> {t.booking.waSend}
                </a>
                <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
                  <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 font-semibold text-forest-800 hover:text-forest-600" data-testid="booking-call-link"><Phone size={15} /> {t.booking.callUs} · {PHONE_DISPLAY}</a>
                  <button onClick={reset} className="inline-flex items-center gap-2 font-semibold text-forest-700 hover:text-forest-900" data-testid="booking-again-btn">
                    <ArrowLeft size={15} /> {t.booking.newBooking}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {step <= 5 && (
            <div className="mt-9 flex items-center justify-between">
              <Button variant="ghost" onClick={back} disabled={step === 1} data-testid="booking-back-btn" className="text-forest-800 hover:text-forest-950 hover:bg-mint disabled:opacity-0">
                <ArrowLeft size={16} /> {t.booking.back}
              </Button>
              {step < 5 ? (
                <Button onClick={next} data-testid="booking-next-btn" className="rounded-full bg-forest-700 hover:bg-forest-800 px-7 py-3.5 h-auto text-white">
                  {t.booking.next} <ArrowRight size={16} />
                </Button>
              ) : (
                <Button onClick={confirm} disabled={sending} data-testid="booking-confirm-btn" className="rounded-full bg-forest-700 hover:bg-forest-800 px-7 py-3.5 h-auto text-white">
                  <Check size={16} /> {t.booking.confirm}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}